import { Link } from "react-router-dom";
import { useSite } from "../context/SiteContext";
import { NetworkCanvas } from "../components/NetworkCanvas";
import { StatsStrip } from "../components/StatsStrip";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { IconArrow } from "../components/Icons";
import { ServicesGrid, PillarsSection, IndustriesGrid, CompliancePreview, ApproachSteps } from "../components/sections";
import { mediaUrl } from "../lib/api";

const PreviewHead = ({ eyebrow, title, lead, to, linkLabel, light }) => (
  <div className="preview-head">
    <Reveal className="sec-head">
      <span className={`eyebrow ${light ? "light" : ""}`}>{eyebrow}</span>
      <h2>{title}</h2>
      {lead && <p>{lead}</p>}
    </Reveal>
    {to && (
      <Link to={to} className="link-arrow" data-testid={`preview-link-${to.replace(/\//g, "")}`}>
        {linkLabel} <IconArrow />
      </Link>
    )}
  </div>
);

export default function Home() {
  const { settings } = useSite();
  const logo = settings?.logo_path || "/lemur-logo.png";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="hero">
        <div className="watermark" style={{ left: "-140px", top: "10%", width: "520px", height: "520px", opacity: 0.04 }} aria-hidden="true">
          <img src={logo} alt="" />
        </div>
        <div className="wrap hero-grid">
          <div>
            <span className="eyebrow">{settings?.hero_eyebrow || "Quality · Compliance · Confidence"}</span>
            <h1>
              {settings?.hero_headline_line1 || "Data. Quality."}<br />
              <span className="g">{settings?.hero_headline_accent || "Better decisions."}</span>
            </h1>
            <p className="lead">{settings?.hero_lead}</p>
            <div className="hero-cta">
              <Link to="/contact?intent=consultation" className="btn btn-primary" data-testid="hero-consultation">Request a Consultation</Link>
              <Link to="/services" className="btn btn-ghost" data-testid="hero-services">Explore services</Link>
            </div>
            <div className="hero-tagline" data-testid="hero-tagline">{settings?.tagline_full || "ENSURING QUALITY. ENABLING TRADE"}</div>
          </div>
          <NetworkCanvas />
        </div>
      </section>

      <StatsStrip />

      {/* COMPANY PREVIEW */}
      <section className="section bg-paper">
        <div className="wrap intro-grid">
          <Reveal className="intro-copy">
            <span className="eyebrow">Who we are</span>
            <h2 style={{ fontSize: "clamp(1.9rem,3.6vw,2.7rem)", margin: "16px 0 18px" }}>
              Specialists in quality, compliance, and operational trust
            </h2>
            <p>Lemur Quality is a specialized consultancy that helps healthcare and livestock organizations understand, implement, and maintain adherence to complex regulatory requirements and industry standards.</p>
            <p>We turn regulatory complexity into clear, workable systems — so organizations can operate with confidence across Somalia and the Horn of Africa.</p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 26 }}>
              <Link to="/about" className="btn btn-ghost" data-testid="home-about-link">About Lemur Quality</Link>
            </div>
          </Reveal>
          <Reveal className="mission" delay={80}>
            <span className="eyebrow">Our mission</span>
            <h3>Empowering professionals to advance quality and innovation</h3>
            <p>To empower researchers and industry professionals with top-quality laboratory capability and compliance expertise that enhances scientific research, protects public health, and supports global innovation and trade.</p>
            {settings?.home_photo_media_id && (
              <figure className="photo-card" style={{ marginTop: 22 }} data-testid="home-field-photo">
                <img src={mediaUrl(settings.home_photo_media_id)} alt={settings.home_photo_alt || ""} loading="lazy" />
              </figure>
            )}
          </Reveal>
        </div>
      </section>

      {/* OUR COMMITMENT */}
      <section className="section dark-sec" data-testid="home-commitment" style={{ padding: "64px 0" }}>
        <div className="wrap" style={{ maxWidth: 900, textAlign: "center" }}>
          <Reveal>
            <span className="eyebrow light" style={{ justifyContent: "center" }}>Our Commitment</span>
            <p style={{ fontFamily: "'Space Grotesk'", fontWeight: 600, fontSize: "clamp(1.15rem,2.3vw,1.55rem)", lineHeight: 1.5, color: "#fff", margin: "20px auto 16px" }} data-testid="commitment-text">
              {settings?.commitment_text || "To deliver reliable, professional, and innovative solutions that promote quality, protect public health, and support sustainable economic growth."}
            </p>
            <p style={{ fontFamily: "'IBM Plex Mono'", fontSize: ".92rem", letterSpacing: ".12em", fontWeight: 600, background: "var(--grad)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", margin: 0 }} data-testid="commitment-tagline">
              {settings?.commitment_tagline || "Ensuring Quality, Enabling Trade."}
            </p>
          </Reveal>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="section bg-mist">
        <div className="wrap">
          <PreviewHead eyebrow="Our operations" title="What we do" lead="Six core service lines delivering quality, compliance, and confidence." to="/services" linkLabel="All services" />
          <ServicesGrid />
        </div>
      </section>

      {/* COMPLIANCE PREVIEW */}
      <section className="section bg-paper">
        <div className="wrap">
          <PreviewHead eyebrow="Compliance consulting" title="Tailored plans for your role in the marketplace" lead="Two specialized practices, one standard of rigor." to="/compliance" linkLabel="All compliance services" />
          <CompliancePreview />
        </div>
      </section>

      {/* INDUSTRIES PREVIEW */}
      <section className="section bg-mist">
        <div className="wrap">
          <PreviewHead eyebrow="Industries & sectors" title="Where our expertise applies" lead="Solution-driven support across scientific and industrial domains." to="/industries" linkLabel="All industries" />
          <IndustriesGrid />
        </div>
      </section>

      {/* WHY PREVIEW */}
      <section className="section dark-sec">
        <div className="wrap">
          <PreviewHead eyebrow="Why Lemur Quality" title="Quality. Compliance. Confidence." lead="The strengths that set our work apart." to="/about/why-lemur-quality" linkLabel="Why Lemur Quality" light />
          <PillarsSection />
        </div>
      </section>

      {/* APPROACH PREVIEW */}
      <section className="section bg-mist">
        <div className="wrap">
          <PreviewHead eyebrow="How we work" title="A clear path to compliance" lead="A structured, repeatable methodology." to="/about/our-approach" linkLabel="Our approach" />
          <ApproachSteps />
        </div>
      </section>

      {/* RESOURCES TEASER */}
      <section className="section bg-paper">
        <div className="wrap">
          <div className="download-card" data-testid="home-resources-teaser">
            <span className="eyebrow">Resources</span>
            <h3>Insights, publications, and answers</h3>
            <p>Explore our resources — our FAQs, gallery, and a growing space for insights and publications.</p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Link to="/resources" className="btn btn-light" data-testid="teaser-resources">Explore resources</Link>
              <Link to="/resources/faqs" className="btn btn-outline-light" data-testid="teaser-faqs">Read FAQs</Link>
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
